import React from 'react';
import { Card, Container, Row, Col,  } from 'react-bootstrap';
import usePlayerTalents from '../hooks/usePlayerTalents';
import { TalentType } from '../types/talents';
import Button from './Button';
import { useAppTranslation } from '../i18n/useAppTranslation';
/**
 * Temporary debugging component to help identify talent ID issues
 * Add this component to your app temporarily to debug the talent ID mismatch
 */
const TalentDebugger: React.FC = () => {
  const { t } = useAppTranslation("common");
  const {
    availableTalents,
    playerTalents,
    loading,
    upgradeTalent,
    refreshTalents
  } = usePlayerTalents();

  const handleTestUpgrade = async (talentId: TalentType) => {
    console.log('🔍 [TALENT DEBUGGER] Testing upgrade for talent ID:', talentId);
    console.log('🔍 [TALENT DEBUGGER] TalentType enum name:', TalentType[talentId]);
    
    const success = await upgradeTalent(talentId);
    console.log('🔍 [TALENT DEBUGGER] Upgrade result:', success);
  };

  const logAllTalentData = () => {
    console.log('🔍 [TALENT DEBUGGER] === FULL TALENT DATA DUMP ===');
    console.log('🔍 [TALENT DEBUGGER] Available Talents:', availableTalents);
    console.log('🔍 [TALENT DEBUGGER] Player Talents:', playerTalents);
    console.log('🔍 [TALENT DEBUGGER] TalentType Enum:', TalentType);
    
    // Log each available talent with its ID mapping
    availableTalents.forEach((talent, index) => {
      console.log(`🔍 [TALENT DEBUGGER] Available Talent ${index}:`, {
        talentId: talent.talentId,
        enumName: TalentType[talent.talentId],
        name: talent.name,
        fullObject: talent
      });
    });
    
    // Log each player talent with its ID mapping
    playerTalents.forEach((talent, index) => {
      console.log(`🔍 [TALENT DEBUGGER] Player Talent ${index}:`, {
        talentId: talent.talentId,
        enumName: TalentType[talent.talentId],
        name: talent.name,
        fullObject: talent
      });
    });
  };

  return (
    <Container className="mt-4">
      <Card bg="dark" text="light">
        <Card.Header>
          <h4>{t("debug.talent.title")}</h4>
          <small className="text-muted">
            {t("debug.talent.temporary")}
          </small>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12}>
              <div className="d-flex gap-2 mb-3">
                <Button variant="wide-primary" onClick={logAllTalentData}>
                  {t("debug.talent.actions.logAll")}
                </Button>
                <Button variant="wide" onClick={refreshTalents} disabled={loading}>
                  {t("debug.talent.actions.refresh")}
                </Button>
              </div>
            </Col>
            
            <Col md={6}>
              <h5>{t("debug.talent.available")}</h5>
              <div className="bg-dark p-3 rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {availableTalents.length === 0 ? (
                  <p className="text-muted">{t("debug.talent.empty.available")}</p>
                ) : (
                  availableTalents.map((talent, index) => (
                    <div key={talent.talentId} className="mb-2 p-2 border rounded">
                      <div><strong>{t("debug.talent.labels.index")}:</strong> {index}</div>
                      <div><strong>{t("debug.talent.labels.talentId")}:</strong> {talent.talentId}</div>
                      <div><strong>{t("debug.talent.labels.enumName")}:</strong> {TalentType[talent.talentId]}</div>
                      <div><strong>{t("debug.talent.labels.name")}:</strong> {talent.name}</div>
                      <Button
                        size="small"
                        variant="wide-primary"
                        className="mt-1"
                        onClick={() => handleTestUpgrade(talent.talentId)}
                      >
                        {t("debug.talent.actions.testUpgrade", { id: talent.talentId })}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </Col>
            
            <Col md={6}>
              <h5>{t("debug.talent.player")}</h5>
              <div className="bg-dark p-3 rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {playerTalents.length === 0 ? (
                  <p className="text-muted">{t("debug.talent.empty.player")}</p>
                ) : (
                  playerTalents.map((talent, index) => (
                    <div key={talent.talentId} className="mb-2 p-2 border rounded">
                      <div><strong>{t("debug.talent.labels.index")}:</strong> {index}</div>
                      <div><strong>{t("debug.talent.labels.talentId")}:</strong> {talent.talentId}</div>
                      <div><strong>{t("debug.talent.labels.enumName")}:</strong> {TalentType[talent.talentId]}</div>
                      <div><strong>{t("debug.talent.labels.name")}:</strong> {talent.name}</div>
                      <div><strong>{t("common:labels.level")}:</strong> {talent.level}</div>
                      <div><strong>{t("debug.talent.labels.recordId")}:</strong> {talent.recordId}</div>
                      <Button
                        size="small"
                        variant="wide-primary"
                        className="mt-1"
                        onClick={() => handleTestUpgrade(talent.talentId)}
                      >
                        {t("debug.talent.actions.testUpgrade", { id: talent.talentId })}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </Col>
            
            <Col xs={12}>
              <h5>{t("debug.talent.enumReference")}</h5>
              <div className="bg-dark p-3 rounded">
                <div className="row">
                  {Object.keys(TalentType)
                    .filter((key) => Number.isNaN(Number(key)))
                    .map((key) => (
                      <div className="col-md-4 mb-1" key={key}>
                        <code>
                          {key} = {TalentType[key as keyof typeof TalentType]}
                        </code>
                      </div>
                    ))}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TalentDebugger;
